import { r as createLogger } from "../logger-BU8YqMGU.js";
import { SESSION_IMAGES_DIR, formatActionFilename, formatFrameFilename, formatTimestamp } from "./file-system-utils.js";
import { writeFileOrAbort } from "./write-file-or-abort.js";
//#region ../src/storage/opfs-storage.ts
/**
* OPFS Storage Module
*
* Uses the Origin Private File System (OPFS) API to persist recording data.
* OPFS works consistently on Desktop Chrome, Android Chrome, and iOS Safari,
* unlike showDirectoryPicker which has write restrictions on Android.
*
* Directory structure (flat sessions — no scenario hierarchy):
* /gps-plus-slam/
*   └── sessions/
*       ├── recording-YYYY-MM-DD_HH-MM-SSutc/
*       │   ├── session.json
*       │   ├── actions/
*       │   │   ├── 000001.json
*       │   │   └── ...
*       │   └── images/        (legacy recordings: frames/)
*       │       ├── frame-000001.jpg
*       │       └── ...
*       └── ...
*
* Scenario grouping (scenarios/{name}/recording-…) is a recorder-only concern,
* handled by ScenarioWrappingStorageBackend in the recorder app.
*/
const log = createLogger("OPFS");
let opfsRoot = null;
let gpsPlusSlamDir = null;
let sessionsDir = null;
let currentSessionHandle = null;
let actionsHandle = null;
let framesHandle = null;
/**
* Reset module state - exported for testing only.
* @internal
*/
function resetOpfsStorage() {
	opfsRoot = null;
	gpsPlusSlamDir = null;
	sessionsDir = null;
	currentSessionHandle = null;
	actionsHandle = null;
	framesHandle = null;
}
/**
* Reset session-level handles only.
*
* Preserves opfsRoot/gpsPlusSlamDir/sessionsDir (OPFS stays initialized)
* but clears current session/actions/frames handles so a new session can
* be started fresh.
*/
function resetSessionHandles() {
	currentSessionHandle = null;
	actionsHandle = null;
	framesHandle = null;
}
/**
* Initialize OPFS storage by creating the required directory structure.
*
* Creates:
* - /gps-plus-slam/
* - /gps-plus-slam/sessions/
*
* This is idempotent - calling multiple times is safe.
*
* @throws Error if OPFS is not supported
*/
async function initOpfsStorage() {
	if (typeof navigator === "undefined" || !navigator.storage || typeof navigator.storage.getDirectory !== "function") throw new Error("OPFS (Origin Private File System) is not supported in this browser. Please use Chrome 86+, Safari 15.2+, or Firefox 111+.");
	opfsRoot = await navigator.storage.getDirectory();
	log.info("OPFS root obtained");
	gpsPlusSlamDir = await opfsRoot.getDirectoryHandle("gps-plus-slam", { create: true });
	sessionsDir = await gpsPlusSlamDir.getDirectoryHandle("sessions", { create: true });
	log.info("OPFS storage initialized");
}
/**
* Get the sessions root directory handle.
* Used by consumers that need direct OPFS access (e.g. ZIP export).
*/
function getSessionsRootHandle() {
	return sessionsDir;
}
/**
* Get the app root directory handle (gps-plus-slam/).
* Used by consumers that need to create additional directories alongside sessions/.
*/
function getAppRootHandle() {
	return gpsPlusSlamDir;
}
/**
* True when the name `name` is already taken under `dir` (by a session
* directory — or by a file, which the follow-up `{ create: true }` call could
* not replace either). Used by {@link createSession} to avoid silently reusing
* a directory when two recordings start within the same (second-resolution)
* timestamp. Rethrows unexpected probe errors (storage failures) so the caller
* fails loudly instead of mislabeling the name.
*/
async function sessionDirExists(dir, name) {
	try {
		await dir.getDirectoryHandle(name, { create: false });
		return true;
	} catch (err) {
		if (err instanceof DOMException && err.name === "NotFoundError") return false;
		if (err instanceof DOMException && err.name === "TypeMismatchError") return true;
		throw err;
	}
}
/**
* Create a new recording session.
*
* Creates the directory structure:
* - /gps-plus-slam/sessions/recording-{timestamp}/
* - /gps-plus-slam/sessions/recording-{timestamp}/actions/
* - /gps-plus-slam/sessions/recording-{timestamp}/images/ (legacy: frames/)
*
* The folder name is `recording-{timestamp}` at whole-second resolution; if a
* directory with that name already exists (two recordings started in the same
* UTC second), a numeric suffix (`-2`, `-3`, …) is appended so the new session
* never silently reuses and corrupts the earlier one's directory.
*
* @param timestamp - Session start time (used for folder naming)
* @param _contextTag - Opaque tag; not used by the flat layout but accepted
*   for interface compatibility with wrapping backends
* @returns Session information
* @throws Error if storage not initialized
*/
async function createSession(timestamp, _contextTag) {
	if (!sessionsDir) throw new Error("OPFS storage not initialized. Call initOpfsStorage first.");
	const baseName = `recording-${formatTimestamp(timestamp)}`;
	let sessionName = baseName;
	let suffix = 1;
	while (await sessionDirExists(sessionsDir, sessionName)) {
		suffix += 1;
		sessionName = `${baseName}-${suffix}`;
	}
	currentSessionHandle = await sessionsDir.getDirectoryHandle(sessionName, { create: true });
	actionsHandle = await currentSessionHandle.getDirectoryHandle("actions", { create: true });
	framesHandle = await currentSessionHandle.getDirectoryHandle(SESSION_IMAGES_DIR, { create: true });
	log.info("Session created:", sessionName);
	return { sessionName };
}
/**
* Get the current session directory handle.
* @internal
*/
function getSessionHandle() {
	return currentSessionHandle;
}
/**
* Set the active session handles externally.
*
* Lets a wrapping StorageBackend that creates sessions outside the flat layout
* (e.g. the recorder's `ScenarioWrappingStorageBackend`, which nests sessions
* under a named bucket) reuse opfs-storage's write infrastructure: it creates
* the session directory itself, then hands the handles here so subsequent
* `writeAction` / `writeFrame` / `writeSessionMetadata` calls target them.
*
* @internal
*/
function setSessionHandles(session, actions, frames) {
	currentSessionHandle = session;
	actionsHandle = actions;
	framesHandle = frames;
}
/**
* Write a Redux action to the current session's actions directory.
*
* @param action - The action object to write
* @param index - Sequential action index (1-based)
* @throws Error if no active session
*/
async function writeAction(action, index) {
	if (!actionsHandle) throw new Error("No active session. Call createSession first.");
	const filename = formatActionFilename(index);
	await writeFileOrAbort(await actionsHandle.getFileHandle(filename, { create: true }), JSON.stringify(action, null, 2));
}
/**
* Write a frame blob to the current session's frames directory.
*
* @param blob - Image blob to write
* @param index - Sequential frame index (1-based)
* @throws Error if no active session
*/
async function writeFrame(blob, index) {
	if (!framesHandle) throw new Error("No active session. Call createSession first.");
	const filename = formatFrameFilename(index);
	await writeFileOrAbort(await framesHandle.getFileHandle(filename, { create: true }), blob);
}
/**
* Write session metadata to session.json in the current session directory.
*
* @param metadata - Session metadata object
* @throws Error if no active session
*/
async function writeSessionMetadata(metadata) {
	if (!currentSessionHandle) throw new Error("No active session. Call createSession first.");
	await writeFileOrAbort(await currentSessionHandle.getFileHandle("session.json", { create: true }), JSON.stringify(metadata, null, 2));
	log.info("Session metadata written");
}
/**
* List all session names in OPFS storage.
*
* @returns Array of session folder names
*/
async function listSessions() {
	if (!sessionsDir) return [];
	const sessions = [];
	for await (const entry of sessionsDir.values()) if (entry.kind === "directory") sessions.push(entry.name);
	return sessions;
}
/**
* Check storage quota.
*
* @returns Available and used storage in bytes
*/
async function checkStorageQuota() {
	if (typeof navigator === "undefined" || !navigator.storage || typeof navigator.storage.estimate !== "function") {
		log.warn("Storage Manager API not available, returning zero quota");
		return {
			available: 0,
			used: 0
		};
	}
	const estimate = await navigator.storage.estimate();
	return {
		available: estimate.quota ?? 0,
		used: estimate.usage ?? 0
	};
}
//#endregion
export { checkStorageQuota, createSession, getAppRootHandle, getSessionHandle, getSessionsRootHandle, initOpfsStorage, listSessions, resetOpfsStorage, resetSessionHandles, setSessionHandles, writeAction, writeFrame, writeSessionMetadata };
